import {type PrimaryLineType} from '../../../util/rampFeatureFlags/linePlotDynamicConfigs';

export type SafePoint = {
  x: number;
  y: number;
  expressionValues?: Record<string, number>;
};

export type Point = {
  x: number;
  y: PossibleNonFinite;
  expressionValues?: Record<string, PossibleNonFinite>;
};

export type AreaPoint = {
  x: number;
  y: PossibleNonFinite;
  y0: PossibleNonFinite;
  expressionValues?: Record<string, PossibleNonFinite>;
};
export type SafeAreaPoint = {
  x: number;
  y: number;
  y0: number;
  expressionValues?: Record<string, number>;
};

export type NonFinitePoint = {
  x: number;
  y: NonFinite;
};

export type BackendLine = {
  areaLine: SafeAreaPoint[];
  historyLine: SafePoint[];
  smoothedLine?: SafePoint[];
  nonFiniteLine: NonFinitePoint[];
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  xMetric: string;
  yMetric: string;
};

export function isAreaPoint<T>(
  point: T extends AreaPoint ? AreaPoint : Point
): point is AreaPoint {
  // @ts-expect-error this is the test
  if (point.y0 === undefined || point.y0 === null) {
    return false;
  }
  return true;
}

export function isSafePoint(point: Point): point is SafePoint {
  return isFinite(Number(point.y));
}

export function isSafeAreaPoint(point: AreaPoint): point is SafeAreaPoint {
  return isFinite(Number(point.y)) && isFinite(Number(point.y0));
}

export type NonFinite = 'Infinity' | 'NaN' | '-Infinity';
export type PossibleNonFinite = number | NonFinite;

export type PointType = 'Last' | 'Avg' | 'Min' | 'Max';
export type LineAggregation = 'Avg' | 'minMax' | 'stdDev' | 'stdErr';
export type HistoryAggKey = `${string}${PointType}`;
type HistoryNaNKey = `${string}HasNaN`;
type HistoryBandKey = `${string}StdDev` | `${string}StdErr`;
type HistorySumSqDevKey = `${string}SumSqDev`;
type HistoryCountKey = `${string}Count`;
export type HistoryFallThroughKey = `${string}""`;
export type ValidHistoryKey =
  | HistoryAggKey
  | HistoryBandKey
  | HistorySumSqDevKey
  | HistoryCountKey
  | HistoryFallThroughKey;
type HistoryRecordKey =
  | 'bucketSize'
  | HistoryNaNKey
  | HistoryAggKey
  | HistoryBandKey
  | HistorySumSqDevKey
  | HistoryCountKey
  | HistoryFallThroughKey;

/**
 * This is the data structure that is returned from the API for full fidelity bucketing. This type maps out:
 * - bucketSize: number (the size of the bucket)
 * - [metric]HasNan: boolean (if the bucket includes a NaN value)
 * - [metric]Avg|Min|Max: number | 'Infinity' | '-Infinity' | 'NaN' (the possible values for a metric)
 * - [metric]StdDev|StdErr: number | 'Infinity' | '-Infinity' | 'NaN'
 * - [metric]SumSqDev: number | 'Infinity' | '-Infinity' | 'NaN' (sum of squared deviations from the mean. Omitted, with Count, when Count is below 2 or the writer did not compute the moment. A present 0 is a computed moment.)
 * - [metric]Count: number (finite samples of this metric, excluding NaN and infinities. Omitted with SumSqDev. Not bucketSize)
 * - [metric]"" - a fall through since the API can also return certain values w/out the bucketing suffixes and aggregations (namely system metrics)
 */
export type HistoryRecord = {
  [K in HistoryRecordKey]: K extends HistoryNaNKey
    ? boolean
    : K extends 'bucketSize' | HistoryCountKey
      ? number
      : K extends HistoryAggKey | HistoryBandKey | HistorySumSqDevKey
        ? PossibleNonFinite
        : K extends HistoryFallThroughKey
          ? number
          : never;
};

export type SupplementalMetrics = Record<string, number>;
export type SupplementalMetricsByRun = Record<string, SupplementalMetrics>;

// Pure data (no methods): it travels through the query-state pipeline, so
// key/metric probes live in util.ts (columnsHaveMetric) instead.
export type HistoryColumns = {
  numRows: number;
  // keyed by full field name, e.g. 'lossAvg', '_stepLast', 'lossHasNaN'
  cols: Record<string, Float64Array | Uint8Array>;
};

// Shared shape for both parsers; the dispatcher passes it to each.
export type ParseHistoryConfig = {
  agg: LineAggregation;
  startTime: {
    value: number;
    unit: 'seconds' | 'milliseconds';
  };
  // we don't need to extend the point values if we're not computing any expressions
  usingExpressions: boolean;
  // when 'min-max-last', expand each bucket into 3 anchors
  primaryLineType?: PrimaryLineType;
};
